import { useCallback, useMemo, useRef } from "react";
import WfEmployeeSelect from "./WfEmployeeSelect";
import { rosterTheme as t } from "./rosterTheme";

/**
 * Search-and-add employees with visible chips (code + name). Values are normalized string IDs.
 */
export default function WfEmployeeMultiSelect({
  value = [],
  onChange,
  disabled,
  maxHeight = 160,
}) {
  const selectKey = useRef(0);

  const picks = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((p) => ({
      employee_id: String(p.employee_id),
      label: p.label || p.employee_name || p.employee_code || "Employee",
      sub: p.sub || p.employee_code || "",
    }));
  }, [value]);

  const idSet = useMemo(() => new Set(picks.map((p) => p.employee_id)), [picks]);

  const addEmployee = useCallback(
    (_, emp) => {
      if (!emp?.employee_id) return;
      const id = String(emp.employee_id);
      if (idSet.has(id)) return;
      const parts = [emp.first_name, emp.last_name].filter(Boolean);
      const name = parts.join(" ").trim() || emp.display_name || emp.employee_code || "Employee";
      onChange([
        ...picks,
        {
          employee_id: id,
          label: name,
          sub: emp.employee_code || "",
          employee_name: name,
          employee_code: emp.employee_code,
        },
      ]);
      selectKey.current += 1;
    },
    [idSet, onChange, picks]
  );

  const removeId = (id) => {
    onChange(picks.filter((p) => p.employee_id !== id));
  };

  return (
    <div className="space-y-2">
      <WfEmployeeSelect
        key={selectKey.current}
        onChange={addEmployee}
        disabled={disabled}
        placeholder="Search and add employee…"
      />
      <div
        className="rounded-lg border border-slate-200 bg-slate-50/80 p-2"
        style={{ maxHeight, overflow: "auto" }}
      >
        {picks.length === 0 ? (
          <p className="text-xs text-slate-500 px-1 py-2">No employees selected yet. Search above to add.</p>
        ) : (
          <ul className="space-y-1">
            {picks.map((p) => (
              <li
                key={p.employee_id}
                className="flex items-center justify-between gap-2 rounded-md bg-white border border-slate-100 px-2 py-1.5 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.label}</div>
                  {p.sub ? <div className="text-xs text-slate-500">{p.sub}</div> : null}
                </div>
                {!disabled && (
                  <button
                    type="button"
                    className={`${t.btnGhost} !text-xs !py-0.5 shrink-0`}
                    onClick={() => removeId(p.employee_id)}
                    aria-label={`Remove ${p.label}`}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span>
          <strong>{picks.length}</strong> employee{picks.length === 1 ? "" : "s"} selected
        </span>
        {picks.length > 0 && !disabled && (
          <button type="button" className="text-red-600 hover:underline" onClick={() => onChange([])}>
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
